/**
 * The one place the app talks to the Intercom Messenger.
 *
 * `index.html` defines `window.Intercom` for every visitor, so "is Intercom in window" says nothing
 * about whether this person may chat. The Messenger only starts once `IntercomUpdate` decides they
 * may (see `useSupportChatAllowed`), and every way of opening chat goes through `openIntercomChat`,
 * which does nothing until then. That is what keeps a program's clients out of chat when their
 * program withholds it, including the in-app "contact us" buttons the launcher setting cannot see.
 *
 * Signed out, the app cannot ask the server who the visitor is, so the last answer it had is kept
 * in localStorage (`rememberSupportChatWithheld`): '1' withheld, '0' allowed, nothing when no one
 * was identified. Client sign-in opens the emailed link in a new tab, which is why it is not
 * sessionStorage. When storage cannot be read, chat stays closed.
 */

const SUPPORT_CHAT_WITHHELD_STORAGE_KEY = 'waterlily-support-chat-withheld';

let isMessengerBooted = false;

export function updateIntercomUser(
  user: {
    name: string;
    email: string;
    createdAt: number;
    userId: string;
    userHash: string;
    clientAdlCount?: number;
    clientPartOfGroupPlan?: boolean;
    userType?: string | null;
  },
  path: string,
) {
  window.Intercom('update', {
    name: user.name, // Full name
    email: user.email, // the email for your user
    created_at: user.createdAt, // Signup date as a Unix timestamp
    user_id: user.userId, // User ID
    user_hash: user.userHash, // Identity Verification HMAC
    client_adl_count: user.clientAdlCount ?? null,
    client_part_of_group_plan: user.clientPartOfGroupPlan ?? null,
    user_type: user.userType ?? null,

    path,
  });
}

export function initializeIntercom() {
  if (isMessengerBooted) {
    return;
  }

  isMessengerBooted = true;
  window.Intercom('boot', {
    app_id: 'kx3qn1mv',
    api_base: 'https://api-iam.intercom.io',
  });
}

export function shutdownIntercom() {
  if (!isMessengerBooted) {
    return;
  }

  isMessengerBooted = false;
  window.Intercom('shutdown');
}

export function isIntercomMessengerBooted(): boolean {
  return isMessengerBooted;
}

export function openIntercomChat() {
  if (!isMessengerBooted) {
    return;
  }

  window.Intercom('show');
}

export function rememberSupportChatWithheld(withheld: boolean) {
  try {
    localStorage.setItem(SUPPORT_CHAT_WITHHELD_STORAGE_KEY, withheld ? '1' : '0');
  } catch {
    // Storage blocked: nothing is remembered, and reading it back keeps chat closed.
  }
}

export function isSupportChatRememberedAsWithheld(): boolean {
  try {
    return localStorage.getItem(SUPPORT_CHAT_WITHHELD_STORAGE_KEY) === '1';
  } catch {
    return true;
  }
}

export function isSupportChatRememberedAsAllowed(): boolean {
  try {
    return localStorage.getItem(SUPPORT_CHAT_WITHHELD_STORAGE_KEY) === '0';
  } catch {
    return false;
  }
}
